<script setup>
import { onBeforeUnmount, watch } from 'vue'
import { CheckCircle2, X } from 'lucide-vue-next'
import serviceQr from '../assets/wechat-service-qr.png'

const props = defineProps({
  open: Boolean,
  courseTitle: { type: String, default: '' },
  price: { type: [String, Number], default: '' }
})
const emit = defineEmits(['close'])

function close() {
  emit('close')
}

function onKeydown(event) {
  if (event.key === 'Escape') close()
}

watch(() => props.open, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
}, { immediate: true })

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="purchase-dialog">
      <div v-if="open" class="purchase-backdrop" @click.self="close">
        <section class="purchase-dialog" role="dialog" aria-modal="true" aria-labelledby="purchase-title">
          <button class="dialog-close" type="button" aria-label="关闭购买咨询" @click="close"><X :size="22" /></button>
          <div class="dialog-copy">
            <p class="dialog-eyebrow">COURSE SERVICE</p>
            <h2 id="purchase-title">咨询购买课程</h2>
            <p class="dialog-lead">网页端不在线收款。请使用微信扫码添加官方客服，确认后为您的登录手机号开通课程。</p>
            <div class="selected-course">
              <span>当前课程</span>
              <strong>{{ courseTitle }}</strong>
              <b v-if="Number(price) > 0">¥{{ Number(price).toFixed(0) }}</b>
            </div>
            <ol class="purchase-steps">
              <li><CheckCircle2 :size="18" /><span>微信扫码添加官方客服</span></li>
              <li><CheckCircle2 :size="18" /><span>发送课程名称和登录手机号</span></li>
              <li><CheckCircle2 :size="18" /><span>客服确认后为账号开通课程</span></li>
            </ol>
            <p class="service-note">如二维码无法识别，可使用页面右下角“联系客服”。</p>
          </div>
          <div class="qr-panel">
            <div class="qr-frame"><img :src="serviceQr" alt="外贸365官方客服微信二维码" /></div>
            <strong>微信扫码咨询</strong>
            <span>添加时请备注“课程购买”</span>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.purchase-backdrop{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;padding:24px;background:rgba(3,18,40,.72);backdrop-filter:blur(4px)}
.purchase-dialog{position:relative;display:grid;grid-template-columns:minmax(0,1fr) 270px;gap:42px;width:min(760px,100%);padding:42px;background:#fff;color:#101828;border:1px solid #d8e0eb;box-shadow:0 28px 80px rgba(2,18,44,.32)}
.dialog-close{position:absolute;top:14px;right:14px;display:grid;place-items:center;width:40px;height:40px;border:1px solid #d8e0eb;background:#fff;color:#0b2447;cursor:pointer}
.dialog-close:hover{border-color:#0965cf;color:#0965cf}.dialog-eyebrow{margin:0 0 12px;color:#0965cf;font-size:13px;font-weight:850;letter-spacing:.13em}.dialog-copy h2{margin:0 0 14px;font-size:32px;line-height:1.2}.dialog-lead{margin:0;color:#596780;line-height:1.75}.selected-course{display:grid;grid-template-columns:1fr auto;gap:5px 14px;margin:24px 0;padding:17px 0;border-top:1px solid #d8e0eb;border-bottom:1px solid #d8e0eb}.selected-course span{grid-column:1/-1;color:#68758d;font-size:13px}.selected-course strong{line-height:1.45}.selected-course b{color:#0066d6;font-size:20px}.purchase-steps{display:grid;gap:13px;margin:0;padding:0;list-style:none}.purchase-steps li{display:flex;align-items:center;gap:10px;font-weight:700}.purchase-steps svg{flex:0 0 auto;color:#087a63}.service-note{margin:22px 0 0;color:#738097;font-size:13px}.qr-panel{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:22px;background:#f3f6fa;text-align:center}.qr-frame{width:190px;padding:10px;background:#fff;border:1px solid #d8e0eb}.qr-frame img{display:block;width:100%;height:auto}.qr-panel strong{margin-top:15px;font-size:18px}.qr-panel span{margin-top:6px;color:#68758d;font-size:13px}.purchase-dialog-enter-active,.purchase-dialog-leave-active{transition:opacity .18s ease}.purchase-dialog-enter-active .purchase-dialog,.purchase-dialog-leave-active .purchase-dialog{transition:transform .18s ease}.purchase-dialog-enter-from,.purchase-dialog-leave-to{opacity:0}.purchase-dialog-enter-from .purchase-dialog,.purchase-dialog-leave-to .purchase-dialog{transform:translateY(10px)}
@media(max-width:680px){.purchase-backdrop{padding:14px}.purchase-dialog{grid-template-columns:1fr;gap:24px;max-height:calc(100vh - 28px);overflow-y:auto;padding:34px 24px 24px}.dialog-copy h2{font-size:27px}.qr-panel{padding:18px}.qr-frame{width:172px}}
@media(prefers-reduced-motion:reduce){.purchase-dialog-enter-active,.purchase-dialog-leave-active,.purchase-dialog-enter-active .purchase-dialog,.purchase-dialog-leave-active .purchase-dialog{transition:none}}
</style>
